import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useMatchFakturowniaInvoiceMutation,
  useSetFakturowniaInvoiceGroupMutation,
  useRerunFakturowniaMatcherMutation,
  useUnlinkFakturowniaInvoiceMutation,
  usePinInvoiceToContributionMutation,
  useUnpinInvoiceFromContributionMutation,
  FakturowniaInvoicesDocument,
  FakturowniaUnmatchedCountDocument,
  FakturowniaInvoiceDocument,
  StudentDetailDocument,
} from 'core/graphql';

import { fakturowniaReasonKey } from 'core/utils/invoiceIssueReasons';

import { useToasts } from '../Toast/useToasts';
import { evictInvoiceLists } from './evictFinanceLists';

interface IInvoiceMatchingHook {
  /** @deprecated Phase 13 — legacy student-only match. Use `linkToContribution` instead. */
  match: (
    invoiceId: string,
    studentId: string,
    groupId?: string,
  ) => Promise<boolean>;
  /** @deprecated Phase 13 — legacy group reassign. Use `linkToContribution` instead. */
  setGroup: (invoiceId: string, groupId: string | null) => Promise<boolean>;
  /** Re-runs the auto-matcher for one invoice. */
  rerunMatcher: (invoiceId: string) => Promise<boolean>;
  /** Phase 13: drop the Payment ↔ Invoice link (and legacy `linked_*`). */
  unlinkPayment: (invoiceId: string) => Promise<boolean>;
  /** ТЗ 0004: pin invoice straight to a scheduled взнос — no Payment created. */
  linkToContribution: (
    invoiceId: string,
    contributionId: string,
  ) => Promise<boolean>;
  /** ТЗ 0004: drop the invoice ↔ взнос link. */
  unlinkFromContribution: (invoiceId: string) => Promise<boolean>;
  loading: boolean;
}

/**
 * Bundles every linking mutation under one hook so pages don't need to wire
 * six separate hooks and copy-paste the toast plumbing. Each call refetches
 * the affected list/detail queries via Apollo's `refetchQueries` so the UI
 * stays in sync without manual cache patching.
 *
 * ТЗ 0004: `linkToContribution` / `unlinkFromContribution` are the canonical
 * linkage for student money — an invoice pins to a взнос, the hidden Payment
 * is provisioned by the backend when the invoice is paid. The manual
 * payment-level `linkToPayment` / `createPayment` surfaces were removed from
 * the UI (PO decision 2026-07-03); `unlinkPayment` stays to detach invoices
 * linked under the legacy model. The legacy `match` / `setGroup` are kept
 * because the backend still serves them as @deprecated stubs (they only
 * write to `linked_*` legacy columns).
 * Pass `studentId` when calling from the student card so `StudentDetail`
 * refetches and the schedule repaints.
 */
export const useInvoiceMatching = (
  studentId?: string,
): IInvoiceMatchingHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  // Stable across renders so dependent useCallback hooks below stay stable too.
  const refetchQueries = useMemo(() => {
    const queries: Array<
      | { query: typeof FakturowniaInvoicesDocument }
      | { query: typeof StudentDetailDocument; variables: { id: string } }
    > = [
      // NOTE (spec 0008 round 2): the previous no-variables
      // FakturowniaInvoicesDocument entry executed an UNBOUNDED company-wide
      // invoice query on every matching mutation while the visible list is
      // actually the useAllFakturowniaInvoices drain (refreshed via the
      // pages' own refetch()/onLinked callbacks) — removed.
      { query: FakturowniaUnmatchedCountDocument },
    ];
    if (studentId) {
      queries.push({
        query: StudentDetailDocument,
        variables: { id: studentId },
      });
    }
    return queries;
  }, [studentId]);

  // Every linking mutation changes which drain combos an invoice belongs to
  // (needsLinkOnly, student/group cards, other date presets). The active
  // screen converges via its own refetch/refetchQueries; evicting the drained
  // ROOT_QUERY field invalidates every OTHER cached combo so a later
  // cache-first walk can't re-serve stale membership (finance-block audit).
  const [matchMutation, matchState] = useMatchFakturowniaInvoiceMutation({
    refetchQueries,
    update: evictInvoiceLists,
  });
  const [
    setGroupMutation,
    setGroupState,
  ] = useSetFakturowniaInvoiceGroupMutation({
    refetchQueries,
    update: evictInvoiceLists,
  });
  const [rerunMutation, rerunState] = useRerunFakturowniaMatcherMutation({
    refetchQueries,
    update: evictInvoiceLists,
  });
  const [
    unlinkPaymentMutation,
    unlinkPaymentState,
  ] = useUnlinkFakturowniaInvoiceMutation({
    refetchQueries,
    update: evictInvoiceLists,
  });
  const [
    linkContributionMutation,
    linkContributionState,
  ] = usePinInvoiceToContributionMutation({
    refetchQueries,
    update: evictInvoiceLists,
  });
  const [
    unlinkContributionMutation,
    unlinkContributionState,
  ] = useUnpinInvoiceFromContributionMutation({
    refetchQueries,
    update: evictInvoiceLists,
  });

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: { type: ToastTypes.success, title },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  // Backend error strings on the 0004 mutations are machine codes
  // (CONTRIBUTION_HAS_INVOICE, CURRENCY_MISMATCH, …) — translate the known
  // ones to human text, pass anything else through verbatim.
  const translateErrorCode = useCallback(
    (code: string) =>
      t(`pages.studentDetail.contributions.errors.${code}`, {
        defaultValue: code,
      }),
    [t],
  );

  const notifyError = useCallback(
    (title: string, errors?: (string | null)[] | null) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title,
          text:
            errors
              ?.filter((e): e is string => Boolean(e))
              .map(translateErrorCode)
              .join('\n') || undefined,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast, translateErrorCode],
  );

  const match = useCallback(
    async (invoiceId: string, matchStudentId: string, groupId?: string) => {
      try {
        const result = await matchMutation({
          variables: { invoiceId, studentId: matchStudentId, groupId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.matchFakturowniaInvoice;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.matched'));
          return true;
        }
        notifyError(t('pages.fakturownia.toasts.matchFailed'), payload?.errors);
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.matchFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [matchMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const setGroup = useCallback(
    async (invoiceId: string, groupId: string | null) => {
      try {
        const result = await setGroupMutation({
          variables: { invoiceId, groupId: groupId || undefined },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.setFakturowniaInvoiceGroup;
        if (payload?.success) {
          notifySuccess(
            t(
              groupId
                ? 'pages.fakturownia.toasts.groupSet'
                : 'pages.fakturownia.toasts.groupCleared',
            ),
          );
          return true;
        }
        notifyError(t('pages.fakturownia.toasts.groupFailed'), payload?.errors);
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.groupFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [setGroupMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const rerunMatcher = useCallback(
    async (invoiceId: string) => {
      try {
        const result = await rerunMutation({
          variables: { invoiceId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.rerunFakturowniaMatcher;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.rerunDone'));
          return true;
        }
        notifyError(t('pages.fakturownia.toasts.rerunFailed'), payload?.errors);
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.rerunFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [rerunMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const unlinkPayment = useCallback(
    async (invoiceId: string) => {
      try {
        const result = await unlinkPaymentMutation({
          variables: { invoiceId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.unlinkFakturowniaInvoice;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.paymentUnlinked'));
          return true;
        }
        notifyError(
          t('pages.fakturownia.toasts.paymentUnlinkFailed'),
          payload?.errors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.paymentUnlinkFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [unlinkPaymentMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  const linkToContribution = useCallback(
    async (invoiceId: string, contributionId: string) => {
      try {
        const result = await linkContributionMutation({
          variables: { invoiceId, contributionId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.linkInvoiceToContribution;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.contributionLinked'));
          return true;
        }
        // Код отказа (CURRENCY_MISMATCH…) — переводом: `errors` сервера —
        // английский текст для логов.
        if (payload?.errorCode) {
          addToast({
            data: {
              type: ToastTypes.error,
              title: t('pages.fakturownia.toasts.contributionLinkFailed'),
              text: t(fakturowniaReasonKey(payload.errorCode), {
                defaultValue: (payload.errors || []).filter(Boolean).join('\n'),
              }),
            },
            type: GlobalToastTypeNames.FakturowniaMatch,
          });
          return false;
        }
        notifyError(
          t('pages.fakturownia.toasts.contributionLinkFailed'),
          payload?.errors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.contributionLinkFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [
      linkContributionMutation,
      refetchQueries,
      notifySuccess,
      notifyError,
      addToast,
      t,
    ],
  );

  const unlinkFromContribution = useCallback(
    async (invoiceId: string) => {
      try {
        const result = await unlinkContributionMutation({
          variables: { invoiceId },
          refetchQueries: [
            ...refetchQueries,
            { query: FakturowniaInvoiceDocument, variables: { id: invoiceId } },
          ],
        });
        const payload = result.data?.unlinkInvoiceFromContribution;
        if (payload?.success) {
          notifySuccess(t('pages.fakturownia.toasts.contributionUnlinked'));
          return true;
        }
        notifyError(
          t('pages.fakturownia.toasts.contributionUnlinkFailed'),
          payload?.errors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.fakturownia.toasts.contributionUnlinkFailed'), [
          (error as Error).message,
        ]);
        return false;
      }
    },
    [unlinkContributionMutation, refetchQueries, notifySuccess, notifyError, t],
  );

  return {
    match,
    setGroup,
    rerunMatcher,
    unlinkPayment,
    linkToContribution,
    unlinkFromContribution,
    loading:
      matchState.loading ||
      setGroupState.loading ||
      rerunState.loading ||
      unlinkPaymentState.loading ||
      linkContributionState.loading ||
      unlinkContributionState.loading,
  };
};
